import { useEffect, useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import type { TranslateState, TranslateProgress } from "@/lib/translateTelemetry";

/**
 * Translation overlay with live progress bar.
 *
 * - Shows spinner + chunk count (done / total) while streaming
 * - Animated progress bar (0–100%) per chunk
 * - Auto-fades out on done/error/idle
 */
const TranslationOverlay = () => {
  const { currentLang, languages } = useLanguage();
  const [state, setState] = useState<TranslateState>("idle");
  const [progress, setProgress] = useState<TranslateProgress>({ done: 0, total: 0 });
  const [visible, setVisible] = useState(false);
  const langName = languages.find((l) => l.code === currentLang)?.native_name || currentLang;

  useEffect(() => {
    let hideTimer: number | undefined;
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as {
        lang: string;
        state: TranslateState;
        progress?: TranslateProgress;
      };
      setState(detail.state);
      if (detail.progress) setProgress(detail.progress);

      if (detail.state === "loading") {
        if (hideTimer) window.clearTimeout(hideTimer);
        setVisible(true);
      } else {
        // Linger briefly so users see "Fertig"/100%, then fade out
        if (hideTimer) window.clearTimeout(hideTimer);
        hideTimer = window.setTimeout(() => setVisible(false), 600);
      }
    };
    window.addEventListener("translate:state", handler as EventListener);
    return () => {
      window.removeEventListener("translate:state", handler as EventListener);
      if (hideTimer) window.clearTimeout(hideTimer);
    };
  }, []);

  if (!visible) return null;

  const pct =
    progress.total > 0 ? Math.min(100, Math.round((progress.done / progress.total) * 100)) : 0;
  const isDone = state === "done";
  const isError = state === "error";

  return (
    <div
      data-notranslate="true"
      translate="no"
      aria-live="polite"
      role="status"
      className={`pointer-events-none fixed top-20 right-4 z-[60] transition-all duration-300 ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-2"
      }`}
    >
      <div className="glass-card rounded-2xl px-4 py-3 shadow-lg min-w-[220px]">
        <div className="flex items-center gap-2.5 mb-2">
          {!isDone && !isError && (
            <div
              className="w-4 h-4 border-2 border-accent border-t-transparent rounded-full animate-spin"
              aria-hidden="true"
            />
          )}
          {isDone && (
            <div className="w-4 h-4 rounded-full bg-accent flex items-center justify-center" aria-hidden="true">
              <svg viewBox="0 0 12 12" className="w-2.5 h-2.5 text-accent-foreground">
                <path d="M2 6.5l2.5 2.5L10 3" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
          )}
          {isError && (
            <div className="w-4 h-4 rounded-full bg-destructive" aria-hidden="true" />
          )}
          <span className="font-body text-xs text-foreground flex-1">
            {isError ? `${langName} – Fehler` : isDone ? `${langName} – fertig` : `${langName}…`}
          </span>
          {progress.total > 0 && (
            <span className="font-body text-[10px] text-muted-foreground tabular-nums">
              {progress.done}/{progress.total}
            </span>
          )}
        </div>
        <div className="h-1 w-full rounded-full bg-muted overflow-hidden" aria-hidden="true">
          <div
            className={`h-full transition-[width] duration-300 ease-out ${
              isError ? "bg-destructive" : "bg-accent"
            }`}
            style={{ width: `${isDone ? 100 : pct}%` }}
          />
        </div>
      </div>
    </div>
  );
};

export default TranslationOverlay;
